import React from 'react';
import i18n from 'i18n-js';
import { ncFileStatuses, formatTimeEvent, convertTimeLetter, ImageUpload, navigateBack } from '@helpers';
import { scale } from '@components/frame';
import { KEYS_TAGS } from '@components/imageHandling/common';
import { IMAGE_UPLOAD_HEADER_TEXT, SCREEN_BORDER_RADIUS, TILE_CONFIG } from '@v2/pages/common/constants/base.constants';
import Svg from '@styles/icons/svg';
import { LevelSubScreen, Perspective } from '@v2/pages/common/enums';
import { NCFileDetailsScreenName } from '@v2/pages/nc-file/details/navigation/config';
import { NCFileDetailsScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { trackSearchResultTapped } from '@services/tracking';
import { HandleOpenParams, NCFileItem } from '@components/search/ncFiles/types';

// Get localized status text based on ncFileStatus
export const getStatusText = (ncFileStatus: string) => {
  switch (ncFileStatus) {
    case ncFileStatuses.Unknown:
      return i18n.t('Unknown NC File');
    case ncFileStatuses.Ignored:
      return i18n.t('Ignored NC File');
    case ncFileStatuses.Change:
      return i18n.t('Change NC File');
    case ncFileStatuses.Setup:
      return i18n.t('Setup NC File');
    default:
      return null;
  }
};

// Render status icon based on ncFileStatus
export const renderStatusIcon = (ncFileStatus: string) => {
  switch (ncFileStatus) {
    case ncFileStatuses.Unknown:
      return <Svg icon="unknown" width={scale(14)} height={scale(14)} />;
    case ncFileStatuses.Ignored:
      return <Svg icon="ignore" color={'Label/Light/Tertiary'} width={scale(12)} height={scale(12)} />;
    case ncFileStatuses.Change:
      return <Svg icon="change" color={'Status/Light/Change'} width={scale(12)} height={scale(12)} />;
    case ncFileStatuses.Setup:
      return <Svg icon="setup" color={'Status/Light/Setup'} width={scale(12)} height={scale(12)} />;
    default:
      return null;
  }
};

// Trigger image upload modal for NC file
export const triggerImageUpload = (item: { ncFileName: string; ncFileId: string }) => {
  ImageUpload({
    headerText: IMAGE_UPLOAD_HEADER_TEXT.AddToNcFile,
    headerDescription: item.ncFileName,
    tags: [{
      scopeId: KEYS_TAGS.Clamping,
      scopeReferenceId: item.ncFileId,
      isFavorite: true,
    }],
  });
};

// Format scheduled time (runTime + changeTime) per piece
export const formatScheduledTime = (item: { runTimePerPcsSecond?: number; changeTimePerPcsSecond?: number }) => {
  const totalSeconds = (item.runTimePerPcsSecond || 0) + (item.changeTimePerPcsSecond || 0);
  return formatTimeEvent(convertTimeLetter(totalSeconds), false);
};

export const buildNavigationParams = (item: NCFileItem, levelSubScreen = LevelSubScreen.GENERAL) => ({
  mainScreen: NCFileDetailsScreenName,
  subScreen: NCFileDetailsScreen.NC_FILE,
  levelSubScreen,
  params: {
    perspective: Perspective.NC_FILE,
    ncFileId: item.ncFileId,
    ncFileName: item.ncFileName,
    ncFileStatus: item.ncFileStatus,
    selectedImageKey: item.imageHash,
    documentId: item.imageDocumentId,
  },
});

export const buildAttachmentData = (item: NCFileItem, attachImageId: number) => ({
  documentId: attachImageId,
  documentLinks: [
    {
      scopeId: KEYS_TAGS.Clamping,
      scopeReferenceId: item.ncFileId,
      scopeReferenceName: item.ncFileName,
      isFavorite: false,
      isActivated: true,
      isFavoriteChanged: false,
    },
  ],
  documentTags: [],
});

export const handleOpen = ({
  item,
  levelSubScreen = LevelSubScreen.GENERAL,
  isPopupMode,
  onItemSelect,
  attachMode,
  attachImageId,
  updateImageMetadata,
  navigateToMainDetailsScreen,
}: HandleOpenParams) => {
  trackSearchResultTapped('NcFile');

  if (isPopupMode && onItemSelect) {
    onItemSelect(item, 'NcFile');
    return;
  }

  if (attachMode && attachImageId) {
    const backendData = buildAttachmentData(item, attachImageId);
    updateImageMetadata(backendData);
    navigateBack();
    return;
  }

  navigateToMainDetailsScreen(buildNavigationParams(item, levelSubScreen));
};

export const getImageSize = (itemSize: number) => {
  const prop = itemSize / TILE_CONFIG.BASE_WIDTH;
  const newHeight = TILE_CONFIG.BASE_HEIGHT * prop;
  return { width: itemSize, height: newHeight };
};

export const getItemStyle = (isEmpty: boolean, itemSize: number, isSmallMode: boolean) => {
  const style: Record<string, any> = {
    opacity: isEmpty ? 0 : 1,
    marginBottom: scale(isSmallMode ? TILE_CONFIG.MARGIN_BOTTOM_SMALL : TILE_CONFIG.MARGIN_BOTTOM_NORMAL),
  };
  if (itemSize) {
    style.width = itemSize;
  }
  return style;
};

export const getStatusFlags = (ncFileStatus: string) => {
  const isUnknown = ncFileStatus === ncFileStatuses.Unknown;
  const isIgnored = ncFileStatus === ncFileStatuses.Ignored;
  const isChange = ncFileStatus === ncFileStatuses.Change;
  const isSetup = ncFileStatus === ncFileStatuses.Setup;
  const isLinked = !isUnknown && !isIgnored && !isChange && !isSetup;
  const showOverlay = isIgnored || isChange || isSetup;
  return { isUnknown, isIgnored, isChange, isSetup, isLinked, showOverlay };
};

export const buildImageData = (
  item: Record<string, any>,
  imgSize: { width: number; height: number },
  onPress: (e?: any) => void,
  attachMode?: boolean,
) => ({
  image: {
    documentId: item.imageDocumentId,
    imageHash: item.imageHash,
    imageData: {
      isCropped: item.imageData?.isCropped,
      extensionDerivative: item.imageData?.extensionDerivative,
      pageNumber: item.imageData?.pageNumber,
      rotationAngle: item.imageData?.rotationAngle || 0,
      rotationAngleCrop: item.imageData?.rotationAngle || 0,
    },
    width: imgSize.width,
    height: imgSize.height,
    contextId: item.ncFileId,
    contextCode: 'c',
    borderRadius: SCREEN_BORDER_RADIUS,
  },
  onPress,
  showImageControl: true,
  attachMode,
});

export const buildSmallImageData = (
  item: Record<string, any>,
  onPress: (e?: any) => void,
  attachMode?: boolean,
) => ({
  image: {
    documentId: item.imageDocumentId,
    imageHash: item.imageHash,
    imageData: {
      isCropped: item.imageData?.isCropped,
      extensionDerivative: item.imageData?.extensionDerivative,
      pageNumber: item.imageData?.pageNumber,
      rotationAngle: item.imageData?.rotationAngle || 0,
      rotationAngleCrop: item.imageData?.rotationAngle || 0,
    },
    width: scale(TILE_CONFIG.LINE_MODE_WIDTH),
    height: scale(TILE_CONFIG.LINE_MODE_HEIGHT),
    contextId: item.ncFileId,
    contextCode: 'c',
  },
  onPress,
  showImageControl: false,
  attachMode,
});

export const getSmallRowWidth = (effectiveWidth: number | undefined, fallbackWidth: number, isPopupMode?: boolean) =>
  (effectiveWidth || fallbackWidth) - scale(isPopupMode ? 0 : 18) * 2;

export const buildPartInfoText = (item: Record<string, any>) => {
  if (!item.partNumber && !item.partDrawingNumber) return '';
  return `${item.partNumber || ''} • ${item.partDrawingNumber || ''} ${i18n.t('Rev')} ${item.partDrawingRevision || ''} ${i18n.t('Pos')} ${item.partDrawingPosition || ''}`;
};
